import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { api } from '../api/client'
import { useEntities } from '../context/EntitiesContext'
import { PromptPageHeader } from '../components/PromptPageHeader'
import { PromptPageNavigation } from '../components/PromptPageNavigation'
import { SimulationDetail } from './simulation/SimulationDetail'

export function SimulationPage () {
  const { id } = useParams<{ id: string }>()
  const { prompts, folders, simulationSuites, loading: entitiesLoading, refreshSimulationSuites } = useEntities()
  const prompt = id ? prompts.find(p => p.id === id) || null : null
  const currentFolder = prompt?.folderId ? folders.find(f => f.id === prompt.folderId) : null

  const suites = useMemo(() => simulationSuites.filter(s => s.promptId === id), [simulationSuites, id])

  const [selectedSuiteId, setSelectedSuiteId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (suites.length > 0) {
      setSelectedSuiteId(prev => {
        if (prev && suites.some(s => s.id === prev)) return prev
        return suites[0].id
      })
    } else {
      setSelectedSuiteId(null)
    }
  }, [suites])

  const handleCreateSuite = async () => {
    if (!id || !prompt) return
    setCreating(true)
    setError(null)
    try {
      const baseName = `${prompt.name} - simulation`
      let name = baseName
      let i = 1
      while (suites.some(s => s.name === name)) {
        i++
        name = `${baseName} ${i}`
      }

      const newSuite = await api.simulations.create({
        name,
        promptId: prompt.id,
      })
      await refreshSimulationSuites()
      setSelectedSuiteId(newSuite.id)
    } catch {
      setError('Failed to create simulation suite')
    } finally {
      setCreating(false)
    }
  }

  const selectedSuite = suites.find(s => s.id === selectedSuiteId) || null

  if (!prompt) {
    return <div className="text-gray-400">Loading...</div>
  }

  return (
    <div>
      <PromptPageHeader prompt={prompt} currentFolder={currentFolder} />
      <PromptPageNavigation promptId={id!} />

      {error && <div className="alert-error mb-4">{error}</div>}

      <div className="mb-4 flex gap-4 items-center">
        <div className="flex-1 max-w-xs">
          <label className="input-label">Simulation Suite</label>
          <select
            value={selectedSuiteId || ''}
            onChange={e => setSelectedSuiteId(e.target.value)}
            className="input"
            disabled={suites.length === 0}
          >
            {suites.length === 0
              ? <option value="">No simulation suites</option>
              : suites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="pt-6">
          <button onClick={handleCreateSuite} disabled={creating} className="btn btn-secondary">
            {creating ? 'Creating...' : '+ New Suite'}
          </button>
        </div>
      </div>

      {selectedSuite
        ? (
        <SimulationDetail
          key={selectedSuite.id}
          suite={selectedSuite}
        />
          )
        : (
        <div className="text-gray-400">
          {entitiesLoading ? 'Loading simulation suites...' : 'Select or create a simulation suite.'}
        </div>
          )}
    </div>
  )
}
